import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";
import { APPLICATION_STATUSES, money } from "../utils/rentalPortal";

const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeApplications = (payload) =>
  toArray(payload).map((item, index) => ({
    id: item.id || `application-${index}`,
    apartment_name: item.apartment_name || item.property_name || "Apartment Unit",
    monthly_rent: Number(item.monthly_rent || item.rent_price || 0),
    preferred_move_in_date: item.preferred_move_in_date || item.move_in_date || "",
    preferred_viewing_date: item.preferred_viewing_date || "",
    lease_duration: item.lease_duration || "",
    application_status: item.application_status || item.status || "Pending",
    submitted_at: item.submitted_at || item.created_at || "",
    message_to_landlord: item.message_to_landlord || item.message || "",
    viewing_scheduled_at: item.viewing_scheduled_at || "",
    viewing_note: item.viewing_note || "",
    visited_at: item.visited_at || "",
    tenant_viewed_at: item.tenant_viewed_at || "",
    landlord_viewed_at: item.landlord_viewed_at || "",
    documents_review_status: item.documents_review_status || "pending",
    documents_submitted_at: item.documents_submitted_at || "",
    workflow_next_step: item.workflow_next_step || "",
    has_documents: Boolean(item.has_documents),
    admin_note: item.admin_note || "",
  }));

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const statusBadgeClass = (status) => {
  if (status === "Assigned" || status === "Approved") {
    return "border-emerald-300/30 bg-emerald-500/15 text-emerald-200";
  }
  if (status === "Rejected" || status === "Cancelled") {
    return "border-red-300/30 bg-red-500/15 text-red-200";
  }
  if (status === "Documents Submitted") {
    return "border-cyan-300/30 bg-cyan-500/15 text-cyan-200";
  }
  if (status === "Reserved") {
    return "border-sky-300/30 bg-sky-500/15 text-sky-200";
  }
  if (status === "Viewing Scheduled" || status === "Visited" || status === "Documents Required") {
    return "border-indigo-300/30 bg-indigo-500/15 text-indigo-200";
  }
  return "border-amber-300/30 bg-amber-500/15 text-amber-200";
};

const canUploadDocuments = (application) => Boolean(application.tenant_viewed_at && application.landlord_viewed_at && ["Visited", "Documents Required"].includes(application.application_status));

export default function TenantApplications() {
  const { hasLinkedUnit = false } = useOutletContext() || {};
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [loadError, setLoadError] = useState("");
  const [viewingId, setViewingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [documentDrafts, setDocumentDrafts] = useState({});

  const loadApplications = useCallback(async () => {
    setLoading(true);
    setLoadError("");

    try {
      const response = await api.get("/tenant/rental-applications");
      setApplications(normalizeApplications(response.data));
    } catch (error) {
      console.error(error);
      setApplications([]);
      if (error?.response?.status === 405) {
        setLoadError("Backend route is read-only (GET only) or submit route is not enabled.");
      } else {
        setLoadError("Unable to load rental applications from database.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const filteredApplications = useMemo(() => {
    if (statusFilter === "All") return applications;
    return applications.filter(
      (item) => String(item.application_status || "").toLowerCase() === statusFilter.toLowerCase()
    );
  }, [applications, statusFilter]);

  const markViewed = async (application) => {
    setViewingId(application.id); setUploadError(""); setUploadMessage("");
    try {
      await api.put(`/tenant/rental-applications/${application.id}/viewed`);
      await loadApplications();
      setUploadMessage("Your viewing confirmation is saved. Documents unlock when the landlord also confirms.");
    } catch (error) { setUploadError(error?.response?.data?.message || "Could not confirm viewing."); }
    finally { setViewingId(null); }
  };

  const setFile = (applicationId, field, file) => {
    setDocumentDrafts((prev) => ({
      ...prev,
      [applicationId]: {
        ...(prev[applicationId] || {}),
        [field]: file || null,
      },
    }));
  };

  const uploadDocuments = async (application) => {
    const draft = documentDrafts[application.id] || {};
    const formData = new FormData();
    if (draft.barangay_clearance) formData.append("barangay_clearance", draft.barangay_clearance);
    if (draft.valid_id) formData.append("valid_id", draft.valid_id);
    if (draft.proof_of_income) formData.append("proof_of_income", draft.proof_of_income);

    if (![draft.barangay_clearance, draft.valid_id, draft.proof_of_income].some(Boolean)) {
      setUploadError("Select at least one document before uploading.");
      setUploadMessage("");
      return;
    }

    setUploadingId(application.id);
    setUploadError("");
    setUploadMessage("");
    try {
      await api.post(`/tenant/rental-applications/${application.id}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadMessage("Requirements uploaded. The landlord will review them before assigning your unit.");
      setDocumentDrafts((prev) => ({ ...prev, [application.id]: {} }));
      await loadApplications();
    } catch (error) {
      console.error(error);
      setUploadError(
        error?.response?.data?.message || "Unable to upload documents for this application."
      );
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans text-white">
      <video data-workspace-background="true" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" src={bgVideo} autoPlay muted loop playsInline />
      <div className="absolute inset-0 bg-slate-950/92" />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/95 to-teal-950/80" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <header className="mb-6 rounded-xl border border-white/10 bg-slate-950/92 p-6 shadow-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-teal-300">Tenant Applications</p>
          <h1 className="mt-2 text-3xl font-bold text-white">My applications</h1>
          <p className="mt-2 text-sm text-slate-300">
            Apply → Both confirm viewing → Document review → Unit assignment. Check below for your next step.
          </p>
        </header>

        <section className="mb-5 grid gap-3 rounded-xl border border-white/10 bg-slate-950/92 p-4 shadow-2xl md:grid-cols-[220px_1fr] md:items-center">
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-lg border border-white/10 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
          >
            <option value="All">All Statuses</option>
            {APPLICATION_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <div className="flex flex-wrap gap-2 md:justify-end">
            <button type="button" className="ws-text-link" onClick={loadApplications} disabled={loading}>Refresh status</button>
            {!hasLinkedUnit && <Link
              to="/tenant/apartments"
              className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-white/10"
            >
              View Available Apartments
            </Link>}
            <Link
              to="/tenant/home"
              className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-white/10"
            >
              Tenant Dashboard
            </Link>
          </div>
        </section>

        {uploadMessage ? (
          <div className="mb-4 rounded-xl border border-emerald-300/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
            {uploadMessage}
          </div>
        ) : null}
        {uploadError ? (
          <div className="mb-4 rounded-xl border border-red-300/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {uploadError}
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-xl border border-white/10 bg-slate-950/92 py-10 text-center text-slate-300 shadow-2xl">
            Loading application status...
          </div>
        ) : loadError ? (
          <div className="rounded-xl border border-red-300/30 bg-red-500/10 py-10 text-center text-red-100 shadow-2xl">
            {loadError}
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/20 bg-slate-950/92 py-10 text-center text-slate-300 shadow-2xl">
            {statusFilter === "All" ? "No applications yet. Choose an apartment to get started." : "No applications with this status."}
          </div>
        ) : (
          <div className="grid gap-3">
            {filteredApplications.map((application) => (
              <article
                key={application.id}
                className="rounded-xl border border-white/10 bg-slate-950/94 p-4 shadow-2xl"
              >
                <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 pb-3">
                  <div>
                    <p className="text-lg font-bold text-white">{application.apartment_name}</p>
                    <p className="text-sm text-slate-300">{money(application.monthly_rent)}</p>
                  </div>
                  <p className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusBadgeClass(application.application_status)}`}>
                    {application.application_status}
                  </p>
                </div>

                <div className="mt-3 grid gap-2 text-sm text-slate-200 md:grid-cols-2">
                  <p>Preferred viewing date: <span className="text-slate-300">{application.preferred_viewing_date || "Not requested"}</span></p>
                  <p>
                    Preferred Move-in Date:{" "}
                    <span className="text-slate-300">
                      {application.preferred_move_in_date || "Not specified"}
                    </span>
                  </p>
                  <p>
                    Lease Duration:{" "}
                    <span className="text-slate-300">{application.lease_duration || "Not specified"}</span>
                  </p>

                  <p>Submitted At: <span className="text-slate-300">{formatDateTime(application.submitted_at)}</span></p>
                </div>

                <section className="mt-3 rounded-lg border border-white/10 p-3">
                  <p>Your viewing: {application.tenant_viewed_at ? "Confirmed" : "Waiting for your confirmation"}</p>
                  <p>Landlord viewing: {application.landlord_viewed_at ? "Confirmed" : "Waiting for landlord confirmation"}</p>
                  {!["Assigned", "Approved", "Rejected", "Cancelled"].includes(application.application_status) && !application.tenant_viewed_at && <button type="button" className="mt-3 rounded-lg bg-teal-600 px-4 py-2" disabled={viewingId === application.id} onClick={() => markViewed(application)}>{viewingId === application.id ? "Saving..." : "Mark as viewed"}</button>}
                  {application.documents_review_status === "refused" && <p className="mt-2 text-amber-200">Documents refused. Read the landlord note, correct your documents, and upload again.</p>}
                  {application.application_status === "Documents Approved" && <p className="mt-2 text-emerald-200">Documents approved. Waiting for unit assignment.</p>}
                </section>

                {application.workflow_next_step ? (
                  <p className="mt-3 rounded-lg border border-indigo-300/20 bg-indigo-500/10 px-3 py-2 text-sm text-indigo-100">
                    Next Step: {application.workflow_next_step}
                  </p>
                ) : null}

                {application.admin_note ? (
                  <p className="mt-3 rounded-lg border border-amber-300/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
                    Landlord Note: {application.admin_note}
                  </p>
                ) : null}

                {application.message_to_landlord ? (
                  <p className="mt-3 rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-sm text-slate-300">
                    {application.message_to_landlord}
                  </p>
                ) : null}

                {canUploadDocuments(application) ? (
                  <section className="mt-4 rounded-lg border border-white/10 bg-slate-900/70 p-3">
                    <p className="text-sm font-semibold text-white">Upload Requirements</p>
                    <p className="mt-1 text-xs text-slate-400">
                      Upload one or more files. Supported: JPG, PNG, PDF.
                    </p>
                    <div className="mt-3 grid gap-2 md:grid-cols-3">
                      <label className="rounded-lg border border-white/10 bg-white/5 p-2 text-xs text-slate-300">
                        Barangay Clearance
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(event) =>
                            setFile(application.id, "barangay_clearance", event.target.files?.[0] || null)
                          }
                          className="mt-1 block w-full text-[11px] text-slate-300 file:mr-2 file:rounded file:border file:border-white/20 file:bg-slate-900 file:px-2 file:py-1 file:text-[11px] file:font-semibold file:text-slate-200"
                        />
                      </label>
                      <label className="rounded-lg border border-white/10 bg-white/5 p-2 text-xs text-slate-300">
                        Valid ID
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(event) =>
                            setFile(application.id, "valid_id", event.target.files?.[0] || null)
                          }
                          className="mt-1 block w-full text-[11px] text-slate-300 file:mr-2 file:rounded file:border file:border-white/20 file:bg-slate-900 file:px-2 file:py-1 file:text-[11px] file:font-semibold file:text-slate-200"
                        />
                      </label>
                      <label className="rounded-lg border border-white/10 bg-white/5 p-2 text-xs text-slate-300">
                        Proof of Income
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(event) =>
                            setFile(application.id, "proof_of_income", event.target.files?.[0] || null)
                          }
                          className="mt-1 block w-full text-[11px] text-slate-300 file:mr-2 file:rounded file:border file:border-white/20 file:bg-slate-900 file:px-2 file:py-1 file:text-[11px] file:font-semibold file:text-slate-200"
                        />
                      </label>
                    </div>

                    <button
                      type="button"
                      onClick={() => uploadDocuments(application)}
                      disabled={uploadingId === application.id}
                      className="mt-3 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {uploadingId === application.id ? "Uploading..." : "Upload Documents"}
                    </button>
                  </section>
                ) : null}

                {application.application_status === "Documents Submitted" && application.has_documents ? (
                  <p className="mt-4 rounded-lg border border-emerald-300/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-100">
                    Documents received. Waiting for the landlord to approve or refuse them.
                  </p>
                ) : null}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
